import { useState, useEffect, useMemo, useRef } from 'react';
import { QuizQuestion } from '@shared/types';

interface DateInputProps {
  question: QuizQuestion;
  value: string; // ISO date string (YYYY-MM-DD)
  onChange: (value: string) => void;
  onFinished?: (value: string) => void;
  minAge?: number;
}

interface DateFormat {
  pattern: string;
  placeholder: string;
  separator: string;
  segments: number[]; // lengths of each segment (e.g., [2, 2, 4] for MM/DD/YYYY)
}

// Get locale-specific date format pattern
function getLocaleDateFormat(): DateFormat {
  const testDate = new Date(2024, 11, 31); // Dec 31, 2024
  const formatted = testDate.toLocaleDateString();
  
  // Detect format based on common patterns (slash, hyphen, and dot separators)
  if (formatted.includes('31/12/2024')) {
    return { pattern: 'DD/MM/YYYY', placeholder: 'DD/MM/YYYY', separator: '/', segments: [2, 2, 4] };
  } else if (formatted.includes('31.12.2024')) {
    return { pattern: 'DD.MM.YYYY', placeholder: 'DD.MM.YYYY', separator: '.', segments: [2, 2, 4] };
  } else if (formatted.includes('12/31/2024')) {
    return { pattern: 'MM/DD/YYYY', placeholder: 'MM/DD/YYYY', separator: '/', segments: [2, 2, 4] };
  } else if (formatted.includes('12.31.2024')) {
    return { pattern: 'MM.DD.YYYY', placeholder: 'MM.DD.YYYY', separator: '.', segments: [2, 2, 4] };
  } else if (formatted.includes('2024/12/31')) {
    return { pattern: 'YYYY/MM/DD', placeholder: 'YYYY/MM/DD', separator: '/', segments: [4, 2, 2] };
  } else if (formatted.includes('2024-12-31')) {
    return { pattern: 'YYYY-MM-DD', placeholder: 'YYYY-MM-DD', separator: '-', segments: [4, 2, 2] };
  } else if (formatted.includes('31-12-2024')) {
    return { pattern: 'DD-MM-YYYY', placeholder: 'DD-MM-YYYY', separator: '-', segments: [2, 2, 4] };
  } else if (formatted.includes('12-31-2024')) {
    return { pattern: 'MM-DD-YYYY', placeholder: 'MM-DD-YYYY', separator: '-', segments: [2, 2, 4] };
  }
  // Default to MM/DD/YYYY (US format)
  return { pattern: 'MM/DD/YYYY', placeholder: 'MM/DD/YYYY', separator: '/', segments: [2, 2, 4] };
}

// Auto-format input with separators
function autoFormat(input: string, format: DateFormat): string {
  // Strip non-digits
  const digits = input.replace(/\D/g, '');
  
  const { separator, segments } = format;
  const maxLen = segments.reduce((a, b) => a + b, 0);
  const trimmed = digits.slice(0, maxLen);
  
  let result = '';
  let pos = 0;
  
  for (let i = 0; i < segments.length && pos < trimmed.length; i++) {
    const segmentLen = segments[i];
    const segment = trimmed.slice(pos, pos + segmentLen);
    result += segment;
    pos += segmentLen;
    
    // Add separator if there are more digits and we're not at the last segment
    if (pos < trimmed.length && i < segments.length - 1) {
      result += separator;
    }
  }
  
  return result;
}

// Parse user input to ISO date string
function parseToIso(input: string, pattern: string): string | null {
  const cleaned = input.trim();
  if (!cleaned) return null;

  const parts = cleaned.split(/[/\-.]/);
  if (parts.length !== 3) return null;

  let year: number, month: number, day: number;

  if (pattern.startsWith('DD')) {
    [day, month, year] = parts.map(Number);
  } else if (pattern.startsWith('YYYY')) {
    [year, month, day] = parts.map(Number);
  } else {
    // MM/DD/YYYY or MM-DD-YYYY
    [month, day, year] = parts.map(Number);
  }

  // Handle 2-digit years
  if (year < 100) {
    year += year > 50 ? 1900 : 2000;
  }

  // Validate
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;

  // Create and validate date
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null; // Invalid date (e.g., Feb 30)
  }

  // Return ISO format
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Normalize any date string to ISO format (YYYY-MM-DD)
// Handles various input formats like YYYY/MM/DD, MM/DD/YYYY, DD/MM/YYYY, etc.
function normalizeToIso(input: string): string | null {
  if (!input) return null;
  
  const cleaned = input.trim();
  const parts = cleaned.split(/[/\-.]/);
  
  if (parts.length !== 3) return null;
  
  let year: number, month: number, day: number;
  
  // If first part is 4 digits, assume YYYY-MM-DD or YYYY/MM/DD
  if (parts[0].length === 4) {
    [year, month, day] = parts.map(Number);
  }
  // If last part is 4 digits, could be MM/DD/YYYY or DD/MM/YYYY
  // We'll try to be smart: if first part > 12, it's likely DD/MM/YYYY
  else if (parts[2].length === 4 || Number(parts[2]) > 99) {
    const first = Number(parts[0]);
    const second = Number(parts[1]);
    year = Number(parts[2]);
    
    // If first > 12, it must be day (DD/MM/YYYY)
    if (first > 12) {
      day = first;
      month = second;
    }
    // If second > 12, it must be day (MM/DD/YYYY)
    else if (second > 12) {
      month = first;
      day = second;
    }
    // Ambiguous - default to MM/DD/YYYY (US format)
    else {
      month = first;
      day = second;
    }
  }
  // 2-digit year at end
  else {
    const first = Number(parts[0]);
    const second = Number(parts[1]);
    year = Number(parts[2]);
    year += year > 50 ? 1900 : 2000;
    
    if (first > 12) {
      day = first;
      month = second;
    } else {
      month = first;
      day = second;
    }
  }
  
  // Validate
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;
  
  // Validate the date is real
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Format ISO date string to locale pattern
function formatFromIso(isoDate: string, format: DateFormat): string {
  if (!isoDate) return '';
  
  // First normalize the input in case it's not proper ISO format
  const normalized = normalizeToIso(isoDate);
  if (!normalized) return '';
  
  const [year, month, day] = normalized.split('-');
  if (!year || !month || !day) return '';
  
  const { pattern, separator } = format;
  
  if (pattern.startsWith('DD')) {
    return `${day}${separator}${month}${separator}${year}`;
  } else if (pattern.startsWith('YYYY')) {
    return `${year}${separator}${month}${separator}${day}`;
  }
  // MM/DD/YYYY
  return `${month}${separator}${day}${separator}${year}`;
}

// Calculate age from ISO date string
function calculateAge(isoDate: string): number {
  const birthDate = new Date(isoDate);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

export default function DateInput({ 
  question: _question, 
  value = '', 
  onChange, 
  onFinished,
  minAge
}: DateInputProps) {
  const format = useMemo(() => getLocaleDateFormat(), []);
  const [displayValue, setDisplayValue] = useState(() => formatFromIso(value, format));
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync display value when external value changes
  // Also normalize wrongly formatted values
  useEffect(() => {
    if (!value) {
      setDisplayValue('');
      return;
    }
    
    const normalized = normalizeToIso(value);
    if (normalized) {
      setDisplayValue(formatFromIso(normalized, format));
      // If the value wasn't in proper ISO format, update the parent
      if (value !== normalized) {
        onChange(normalized);
      }
    } else {
      // Can't parse the value, show it raw
      setDisplayValue(value);
    }
  }, [value, format, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const formatted = autoFormat(input, format);
    setDisplayValue(formatted);
    setError(null);
    
    // Auto-add separator when segment is complete
    const digits = input.replace(/\D/g, '');
    const prevDigits = displayValue.replace(/\D/g, '');
    
    // If user just completed a segment, move cursor after the separator
    if (digits.length > prevDigits.length && inputRef.current) {
      const { segments } = format;
      let charCount = 0;
      for (let i = 0; i < segments.length - 1; i++) {
        charCount += segments[i];
        if (digits.length === charCount) {
          // Cursor should be after the separator
          const cursorPos = formatted.length;
          requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(cursorPos, cursorPos);
          });
          break;
        }
      }
    }
  };

  const handleBlur = () => {
    if (!displayValue.trim()) {
      setError(null);
      onChange('');
      return;
    }

    const isoDate = parseToIso(displayValue, format.pattern);
    
    if (!isoDate) {
      setError(`Please enter a valid date (${format.placeholder})`);
      return;
    }

    // Check if date is in the future
    const inputDate = new Date(isoDate);
    if (inputDate > new Date()) {
      setError("Date can't be in the future");
      return;
    }

    if (minAge) {
      const age = calculateAge(isoDate);
      if (age < minAge) {
        setError(`You must be at least ${minAge} years old`);
        return;
      }
    }

    setError(null);
    setDisplayValue(formatFromIso(isoDate, format));
    onChange(isoDate);
    onFinished?.(isoDate);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
  };

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={format.placeholder}
        className={`input text-lg w-full ${error ? 'border-red-500 focus:border-red-500' : ''}`}
        autoComplete="bday"
      />
      {error && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
